import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaChartBar, FaHome, FaShoppingCart, FaBox, FaTruck, FaUsers,
  FaMoneyBillAlt, FaSignOutAlt, FaPlus, FaEdit, FaList, FaHourglassHalf,
  FaCheckCircle, FaHandshake, FaMapMarkerAlt, FaUserPlus, FaEye,
  FaChartLine, FaClipboardList, FaShippingFast, FaTimesCircle, FaUndo,
  FaInfoCircle, FaLeaf, FaSitemap, FaFileAlt, FaTimes, 
  FaUserMd, FaCalendarCheck, FaStethoscope // New Icons for Doctor
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';

interface MenuItem {
  id: number;
  name: string;
  path?: string;
  icon?: string;
  children?: MenuItem[];
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const getIconComponent = (iconName?: string) => {
    switch (iconName) {
    case 'FaHome': return <FaHome />;
    case 'FaChartBar': return <FaChartBar />;
    case 'FaShoppingCart': return <FaShoppingCart />;
    case 'FaBox': return <FaBox />;
    case 'FaTruck': return <FaTruck />;
    case 'FaUsers': return <FaUsers />;
    case 'FaMoneyBillAlt': return <FaMoneyBillAlt />;
    case 'FaSignOutAlt': return <FaSignOutAlt />;
    case 'FaPlus': return <FaPlus />;
    case 'FaEdit': return <FaEdit />;
    case 'FaList': return <FaList />;
    case 'FaHourglassHalf': return <FaHourglassHalf />;
    case 'FaCheckCircle': return <FaCheckCircle />;
    case 'FaHandshake': return <FaHandshake />;
    case 'FaMapMarkerAlt': return <FaMapMarkerAlt />;
    case 'FaUserPlus': return <FaUserPlus />;
    case 'FaEye': return <FaEye />;
    case 'FaChartLine': return <FaChartLine />;
    case 'FaClipboardList': return <FaClipboardList />;
    case 'FaShippingFast': return <FaShippingFast />;
    case 'FaTimesCircle': return <FaTimesCircle />;
    case 'FaUndo': return <FaUndo />;
    case 'FaInfoCircle': return <FaInfoCircle />;
    case 'FaLeaf': return <FaLeaf />;
    case 'FaSitemap': return <FaSitemap />;
    case 'FaFileAlt': return <FaFileAlt />;
    // Doctor Icons
    case 'FaUserMd': return <FaUserMd />;
    case 'FaCalendarCheck': return <FaCalendarCheck />;
    case 'FaStethoscope': return <FaStethoscope />;
    default: return null;
  }
};

// --- ADMIN / STAFF MENU ---
const adminMenuItems: MenuItem[] = [
  {
    id: 1,
    name: 'Dashboard',
    path: '/admin/dashboard',
    icon: 'FaHome',
  },
  {
    id: 2,
    name: 'Category',
    icon: 'FaSitemap',
    children: [
      { id: 21, name: 'Category List', path: '/admin/category/list', icon: 'FaList' },
      { id: 22, name: 'Edit Category', path: '/admin/category/edit', icon: 'FaEdit' },
      { id: 23, name: 'Add Category', path: '/admin/category/add', icon: 'FaPlus' },
    ],
  },
  {
    id: 3,
    name: 'Product',
    icon: 'FaBox',
    children: [
      { id: 31, name: 'Product List', path: '/admin/product/list', icon: 'FaList' },
      { id: 33, name: 'Add Product', path: '/admin/product/add', icon: 'FaPlus' },
    ],
  },
  {
    id: 4,
    name: 'Order',
    icon: 'FaClipboardList',
    children: [
      { id: 42, name: 'Orders Delivered', path: '/admin/order/completed', icon: 'FaCheckCircle' },
      { id: 75, name: 'Orders Placed', path: '/admin/account/orders-placed', icon: 'FaShoppingCart' },
      { id: 72, name: 'Orders in Process', path: '/admin/account/orders-in-process', icon: 'FaUndo' },
      { id: 73, name: 'Orders Cancelled', path: '/admin/account/orders-cancelled', icon: 'FaTimesCircle' },
      { id: 101, name: 'All Orders', path: '/admin/order/all', icon: 'FaList' },
      { id: 102, name: 'Unverified Order', path: '/admin/order/unverified', icon: 'FaList' },
      { id: 103, name: 'Verified Orders', path: '/admin/order/verified', icon: 'FaList' },
    ],
  },
  {
    id: 6,
    name: 'Subordinates',
    icon: 'FaUsers',
    children: [
      { id: 61, name: 'Manage Subordinates', path: '/admin/subordinates/manage', icon: 'FaUserPlus' },
      { id: 62, name: 'Pending Approvals', path: '/admin/subordinates/pending-approvals', icon: 'FaHourglassHalf' },
    ],
  },
  {
    id: 7,
    name: 'Account',
    icon: 'FaMoneyBillAlt',
    children: [
      { id: 71, name: 'Delivery Charges', path: '/admin/account/delivery-charges', icon: 'FaShippingFast' },
      { id: 71, name: 'Delivery Management', path: '/admin/account/set-delivery', icon: 'FaShippingFast' },
      { id: 76, name: 'Sales Report', path: '/admin/account/sales-report', icon: 'FaChartLine' },
    ],
  },
  {
    id: 8,
    name: 'Data',
    icon: 'FaChartBar',
    children: [
      { id: 81, name: 'Hot Lead', path: '/admin/data/hot-data', icon: 'FaInfoCircle' },
      { id: 82, name: 'New Data', path: '/admin/data/new-data', icon: 'FaPlus' },
      { id: 83, name: 'All Order', path: '/admin/order/verified', icon: 'FaList' },
      { id: 84, name: 'Call History', path: '/admin/call-history', icon: 'FaList' },
    ],
  },
   {
    id: 9,
    name: 'Manage Appointments',
    icon: 'FaChartBar',
    children: [
      { id: 90, name: 'Appointments', path: '/admin/appointments/view', icon: 'FaEye' },
      { id: 91, name: 'Add Departments', path: '/admin/appointments/add-depart', icon: 'FaPlus' },
      { id: 92, name: 'Departments List', path: '/admin/appointments/depart-list', icon: 'FaList' },
      { id: 93, name: 'Add Doctors', path: '/admin/appointments/add-doctor', icon: 'FaPlus' },
      { id: 94, name: 'Doctor List', path: '/admin/appointments/doctors-list', icon: 'FaUserMd' },
    ],
  },
];

// --- DOCTOR MENU ---
const doctorMenuItems: MenuItem[] = [
  {
    id: 1,
    name: 'Dashboard',
    path: '/admin/dashboard',
    icon: 'FaHome',
  },
  {
    id: 2,
    name: 'My Appointments',
    path: '/admin/doctor/appointments', // Is route ke liye page banana hoga
    icon: 'FaCalendarCheck',
  },
  {
    id: 3,
    name: 'Patient History',
    path: '/admin/doctor/patients', // Is route ke liye page banana hoga
    icon: 'FaUserMd',
  }
];


const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openSubmenus, setOpenSubmenus] = useState<Set<number>>(new Set());
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Select menu based on Role
  const menuItems = user?.role === 'DOCTOR' ? doctorMenuItems : adminMenuItems;

  const renderMenuItems = (items: MenuItem[]) => {
    return items.map((item) => {
      // Agent Restriction Logic (Existing)
      const isAgent = user?.role === 'AGENT';
      const isAgentAllowedPath = isAgent && (
        item.path === '/admin/data/hot-data' ||
        item.path === '/admin/data/new-data' ||
        item.path === '/admin/dashboard'
      );

      if (isAgent && item.name !== 'Data' && item.name !== 'Dashboard') {
        return null;
      }

      if (isAgent && item.path && !isAgentAllowedPath) {
        return null;
      }

      if (isAgent && item.name === 'Data') {
        const filteredChildren = item.children?.filter(child =>
          child.path === '/admin/data/hot-data' || child.path === '/admin/data/new-data'
        );
        if (filteredChildren?.length === 0) return null; 
        
        // ... (Agent Data Menu rendering logic same as before)
        return (
          <li key={item.id} className="relative">
            <button
              onClick={() => setOpenSubmenus(prev => {
                const newSet = new Set(prev);
                if (newSet.has(item.id)) newSet.delete(item.id);
                else newSet.add(item.id);
                return newSet;
              })}
              className="flex items-center justify-between p-3 w-full text-left rounded-md transition-all duration-200 ease-in-out focus:outline-none text-lg shadow-2xl  hover:shadow-2xl active:translate-y-0.5
              text-gray-700 hover:bg-gray-100 
              dark:text-gray-200 dark:hover:bg-[#1A6A5C]"
            >
              <div className="flex items-center">
                <span className="mr-3">{getIconComponent(item.icon)}</span>
                <span>{item.name}</span>
              </div>
            </button>
            {openSubmenus.has(item.id) && (
              <ul className="pl-8 mt-2 space-y-2">
                {renderMenuItems(filteredChildren!)}
              </ul>
            )}
          </li>
        );
      }

      // Standard Menu Item Rendering
      return (
        <li key={item.id} className="relative" onClick={item.path ? onClose : undefined}>
          {item.children ? (
            <>
              <button
                onClick={() => {
                  setOpenSubmenus((prev) => {
                    const newSet = new Set(prev);
                    if (newSet.has(item.id)) {
                      newSet.delete(item.id);
                    } else {
                      newSet.add(item.id);
                    }
                    return newSet;
                  });
                }}
                className="flex items-center justify-between p-3 w-full text-left rounded-md transition-all duration-200 ease-in-out focus:outline-none text-lg shadow-2xl  hover:shadow-2xl active:translate-y-0.5
                text-gray-700 hover:bg-[#1A6A5C] hover:text-white
                dark:text-gray-200 dark:hover:bg-[#1A6A5C] dark:hover:text-white"
              >
                <div className="flex items-center">
                  <span className="mr-3">{getIconComponent(item.icon)}</span>
                  <span>{item.name}</span>
                </div>
              </button>
              {openSubmenus.has(item.id) && (
                <ul className="pl-8 mt-2 space-y-2">
                  {renderMenuItems(item.children)}
                </ul>
              )}
            </>
          ) : (
            <Link
              to={item.path!}
              className={`flex items-center p-3 rounded-md w-full transition-all duration-200 ease-in-out text-lg shadow-sm hover:shadow-md active:translate-y-0.5
                ${location.pathname === item.path 
                    ? 'bg-[#1A6A5C] text-white shadow-md' 
                    : 'text-gray-700 hover:bg-[#1A6A5C] hover:text-white dark:text-gray-200 dark:hover:bg-[#1A6A5C] dark:hover:text-white' 
                }`}
            >
              <span className="mr-3">{getIconComponent(item.icon)}</span>
              <span>{item.name}</span>
            </Link>
          )}
        </li>
      );
    });
  };

  return (
    <>
      <aside
        className={`w-80 fixed top-0 left-0 h-screen p-4 shadow-xl flex flex-col z-40 transition-transform duration-300 ease-in-out
          bg-white border-r border-gray-200 
          dark:bg-gray-800 dark:border-gray-700 dark:text-white
          transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 lg:flex`}
      >
        <div className="flex-grow overflow-hidden">
          <div className="h-full overflow-y-auto hide-scrollbar pr-4">
            
            <div className="flex items-center justify-between px-5 pb-5 mb-5 border-b border-gray-200 dark:border-[#1A6A5C]">
              <div className="flex items-center">
                <FaLeaf className="h-8 w-8 text-[#1A6A5C] dark:text-[#D3D3D3] mr-2" />
                <span className="text-2xl font-bold text-gray-800 dark:text-white">Ayush Vatika</span>
              </div>
              <button onClick={onClose} className="lg:hidden text-2xl text-gray-500 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white">
                <FaTimes />
              </button>
            </div>

            <nav>
              <ul className="space-y-4">
                {renderMenuItems(menuItems)}
              </ul>
            </nav>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-[#1A6A5C]">
          <button
            onClick={handleLogout}
            className="flex items-center p-2 rounded-md w-full text-left transition-all duration-200 ease-in-out hover:bg-red-600 bg-[#1A6A5C] text-white hover:shadow-lg shadow-md"
          >
            <FaSignOutAlt className="mr-3" />
            Logout
          </button>
        </div>
      </aside>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-30 lg:hidden"
          onClick={onClose}
        ></div>
      )}
    </>
  );
};

export default Sidebar;